/** The URL field's text rules. The live editing ring, the commit gate and
 *  every display site read these same functions, so a draft the ring cleared
 *  is never refused at blur and a stored value always reads the same way. */

/** Whether text is a complete web address. Deliberately looser than
 *  `new URL()`: the scheme is optional (the editor completes it on commit),
 *  and the host needs one dot with a label on each side — `example.d` is
 *  done, `example.` and `example` are not yet. A port, path, query or hash
 *  may follow. Whitespace and non-web schemes never pass. */
const URL_TEXT_PATTERN =
  /^(?:https?:\/\/)?(?:[^\s/?#:@.]+\.)+[^\s/?#:@.]+(?::\d{1,5})?(?:[/?#]\S*)?$/i;

/** Text that already names a scheme: `scheme://`, or `scheme:` followed by
 *  anything but a digit — a digit after the colon is a port on a bare host
 *  (`newb.com:8080`), not a scheme (`mailto:`, `javascript:`). */
const HAS_SCHEME_PATTERN = /^[a-z][a-z\d+.-]*:(?:\/\/|(?!\d))/i;
const WEB_SCHEME_PATTERN = /^https?:\/\//i;

export function isValidUrlText(value: string): boolean {
  return URL_TEXT_PATTERN.test(value.trim());
}

/** The stored form of a URL draft: `https://` completes text typed without
 *  a scheme; text that carries one persists as typed, so an unsupported
 *  scheme reaches the commit gate and is refused there instead of being
 *  silently rewritten. Blank text is unanswered (`null`). */
export function normalizeUrlValue(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (HAS_SCHEME_PATTERN.test(trimmed)) return trimmed;
  return `https://${trimmed.replace(/^\/+/, "")}`;
}

/** An href a browser may open: the normalized value when its scheme is
 *  http(s), `null` for blank text or any other scheme. */
export function urlHref(value: string): string | null {
  const normalized = normalizeUrlValue(value);
  return normalized && WEB_SCHEME_PATTERN.test(normalized) ? normalized : null;
}

/** How a URL reads at rest: the web scheme dropped, everything else kept. */
export function urlDisplayText(value: string): string {
  return value.replace(/^https?:\/\//i, "");
}

/** The host a URL points at (`www.example.com` for
 *  `https://www.example.com/path`), `null` when the text has no parseable
 *  host. Drives per-site glyphs such as a favicon. */
export function urlHost(value: string): string | null {
  const href = urlHref(value);
  if (!href) return null;
  try {
    return new URL(href).hostname || null;
  } catch {
    return null;
  }
}
